import {
  Button,
  Flex,
  Link,
  Typography,
  emailRegex,
  telRegex,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';

import { MarketingConsent } from '../../marketingConsent/MarketingConsent';
import { PasswordField } from '../passwordField/passwordField';

export interface SignUpFormData {
  email: string;
  password: string;
  phone: string;
  promotionsOptIn?: boolean;
}

export interface SignUpProps {
  /** Pre-filled email from the email-entry step (editable). */
  defaultEmail?: string;

  /** Errors from a previous sign-up attempt. */
  errors?: string[] | null;

  /** Function called when the Cancel button is clicked. */
  onCancel(): void;

  /** Function called when the form is submitted. */
  onSubmit(data: SignUpFormData): void;

  /**
   * Whether to render the marketing promotions opt-in checkbox. The opt-in
   * is iheartjane.com-marketplace-specific behavior; embedded menus should
   * leave this off.
   */
  showPromotionsOptIn?: boolean;
}

const PASSWORD_MIN_LENGTH = 10;

export const SignUp = ({
  defaultEmail,
  errors,
  onCancel,
  onSubmit,
  showPromotionsOptIn,
}: SignUpProps) => {
  return (
    <Form
      name="sign-up"
      onSubmit={(data) => onSubmit(data as unknown as SignUpFormData)}
    >
      <Flex width="100%" flexDirection="column" alignItems="center">
        <Typography branded variant="header-bold" textAlign="center" mb={24}>
          Create an account
        </Typography>
        <Form.TextField
          autocomplete="email"
          defaultValue={defaultEmail}
          disableMobileInputStyling
          errorMessage="Please enter a valid email address (e.g., name@example.com)."
          errorOnBlur
          label="Email"
          mb={24}
          name="email"
          pattern={emailRegex}
          required
          requiredLabel
          requiredMessage="Please enter your email address."
          type="email"
        />
        <Form.NumberField
          allowLeadingZeros
          autocomplete="tel"
          autoFocus
          data-testid="phone-number-field"
          disableMobileInputStyling
          errorMessage="Please enter a valid 10-digit phone number (e.g., (555) 123-4567)."
          errorOnBlur
          helperText="Used only for reservation notices"
          label="Phone number"
          maskFormat="(###) ###-####"
          mb={24}
          name="phone"
          pattern={telRegex}
          required
          requiredLabel
          requiredMessage="Please enter your phone number."
          width="100%"
        />
        <PasswordField
          autocomplete="new-password"
          errorMessage={`Your password must be at least ${PASSWORD_MIN_LENGTH} characters.`}
          helperText={`Your password must be at least ${PASSWORD_MIN_LENGTH} characters`}
          minLength={PASSWORD_MIN_LENGTH}
        />

        {showPromotionsOptIn && (
          <Flex mb={24} flexDirection="row">
            <Form.CheckboxField
              defaultChecked
              disableMobileInputStyling
              label="Promotional emails"
              labelHidden
              name="promotionsOptIn"
            />
            <MarketingConsent />
          </Flex>
        )}

        {errors && errors.length > 0 && (
          <Flex flexDirection="column" justifyContent="center" mt={-12}>
            {errors.map((error) => (
              <Typography key={error} color="error" mb={12} mx="auto">
                {error}
              </Typography>
            ))}
          </Flex>
        )}

        <Form.SubmitButton
          data-testid="sign-up-submit-btn"
          full
          label="Continue"
          mb={16}
          variant="primary"
        />
        <Button
          full
          label="Cancel"
          mb={24}
          onClick={onCancel}
          variant="tertiary"
        />
        <Typography color="grays-mid" textAlign="center" variant="body">
          By creating an account, I agree to{' '}
          <Link href="https://www.iheartjane.com/terms">
            Jane Technology&rsquo;s Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="https://www.iheartjane.com/privacy-policy">
            Privacy Policy
          </Link>
          .
        </Typography>
      </Flex>
    </Form>
  );
};
